import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AdminschoolService from "services/AdminschoolService";

const initialState = {
  statistics: null,
  loading: false,
  error: null,
  formationsRevenue: {
    data: null,
    loading: false,
    error: null,
  },
  supportCoursesRevenue: {
    data: null,
    loading: false,
    error: null,
  },
  expenses: {
    data: null,
    loading: false,
    error: null,
    loading: false,
    error: null,
  },
  subscriptionStatistics: {
    data: null,
    loading: false,
    error: null,
  },
  financeSummary: {
    data: null,
    loading: false,
    resetting: false,
    error: null,
  },
};

// Thunk pour récupérer les statistiques globales de l'application
export const getAdminschoolStatistics = createAsyncThunk(
  "adminschool/getStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminschoolService.getStatistics();
      return response.data.statistics;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du chargement des statistiques"
      );
    }
  }
);

export const getSubscriptionStatistics = createAsyncThunk(
  "adminschool/getSubscriptionStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminschoolService.getSubscriptionStatistics();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du chargement des statistiques d'abonnement"
      );
    }
  }
);

export const getFormationsRevenue = createAsyncThunk(
  "adminschool/getFormationsRevenue",
  async (
    { granularity, type, name, year, month, currency },
    { rejectWithValue }
  ) => {
    try {
      const response = await AdminschoolService.getFormationsRevenue({
        granularity,
        type,
        name,
        year,
        month,
        currency,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du chargement des revenus des formations"
      );
    }
  }
);

export const getSupportCoursesRevenue = createAsyncThunk(
  "adminschool/getSupportCoursesRevenue",
  async ({ granularity, type, name, year, month }, { rejectWithValue }) => {
    try {
      const response = await AdminschoolService.getSupportCoursesRevenue({
        granularity,
        type,
        name,
        year,
        month,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du chargement des revenus des cours de support"
      );
    }
  }
);

export const getExpenses = createAsyncThunk(
  "adminschool/getExpenses",
  async ({ granularity, year, month }, { rejectWithValue }) => {
    try {
      const response = await AdminschoolService.getExpenses({
        granularity,
        year,
        month,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du chargement des expenses"
      );
    }
  }
);
export const getFinanceSummary = createAsyncThunk(
  "adminschool/getFinanceSummary",
  async ({ currency }, { rejectWithValue }) => {
    try {
      const response = await AdminschoolService.getFinanceSummary({ currency });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du chargement des compteurs financiers"
      );
    }
  }
);

export const resetFinanceCounters = createAsyncThunk(
  "adminschool/resetFinanceCounters",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminschoolService.resetFinanceCounters();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la remise à zéro des compteurs"
      );
    }
  }
);

const adminschoolSlice = createSlice({
  name: "adminschool",
  initialState,
  reducers: {
    resetStatistics: (state) => {
      state.statistics = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminschoolStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminschoolStatistics.fulfilled, (state, { payload }) => {
        state.statistics = payload;
        state.loading = false;
      })
      .addCase(getAdminschoolStatistics.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      .addCase(getFormationsRevenue.pending, (state) => {
        state.formationsRevenue.loading = true;
        state.formationsRevenue.error = null;
      })
      .addCase(getFormationsRevenue.fulfilled, (state, { payload }) => {
        state.formationsRevenue.data = payload;
        state.formationsRevenue.loading = false;
      })
      .addCase(getFormationsRevenue.rejected, (state, { payload }) => {
        state.formationsRevenue.error = payload;
        state.formationsRevenue.loading = false;
      })
      .addCase(getSupportCoursesRevenue.pending, (state) => {
        state.supportCoursesRevenue.loading = true;
        state.supportCoursesRevenue.error = null;
      })
      .addCase(getSupportCoursesRevenue.fulfilled, (state, { payload }) => {
        state.supportCoursesRevenue.data = payload;
        state.supportCoursesRevenue.loading = false;
      })
      .addCase(getSupportCoursesRevenue.rejected, (state, { payload }) => {
        state.supportCoursesRevenue.error = payload;
        state.supportCoursesRevenue.loading = false;
      })
      .addCase(getExpenses.pending, (state) => {
        state.expenses.loading = true;
        state.expenses.error = null;
      })
      .addCase(getExpenses.fulfilled, (state, { payload }) => {
        state.expenses.data = payload;
        state.expenses.loading = false;
      })
      .addCase(getExpenses.rejected, (state, { payload }) => {
        state.expenses.error = payload;
        state.expenses.loading = false;
      })
      .addCase(getSubscriptionStatistics.pending, (state) => {
        state.subscriptionStatistics.loading = true;
        state.subscriptionStatistics.error = null;
      })
      .addCase(getSubscriptionStatistics.fulfilled, (state, { payload }) => {
        state.subscriptionStatistics.data = payload;
        state.subscriptionStatistics.loading = false;
      })
      .addCase(getSubscriptionStatistics.rejected, (state, { payload }) => {
        state.subscriptionStatistics.error = payload;
        state.subscriptionStatistics.loading = false;
      })
      .addCase(getFinanceSummary.pending, (state) => {
        state.financeSummary.loading = true;
        state.financeSummary.error = null;
      })
      .addCase(getFinanceSummary.fulfilled, (state, { payload }) => {
        state.financeSummary.data = payload;
        state.financeSummary.loading = false;
      })
      .addCase(getFinanceSummary.rejected, (state, { payload }) => {
        state.financeSummary.error = payload;
        state.financeSummary.loading = false;
      })
      .addCase(resetFinanceCounters.pending, (state) => {
        state.financeSummary.resetting = true;
      })
      .addCase(resetFinanceCounters.fulfilled, (state) => {
        state.financeSummary.resetting = false;
      })
      .addCase(resetFinanceCounters.rejected, (state) => {
        state.financeSummary.resetting = false;
      });
  },
});

export const { resetStatistics } = adminschoolSlice.actions;
export default adminschoolSlice.reducer;
